/**
 * @file Toolbar.tsx
 *
 * @changelog
 * v1.0.0
 * - Not available in shadcn, created for RS
 */
import type { VariantProps } from 'tailwind-variants'
import { cn } from 'tailwind-variants'
import { Toolbar as ToolbarPrimitive } from '@base-ui/react/toolbar'

import { buttonVariants } from './button'

function Toolbar({
  className,
  orientation = 'horizontal',
  loopFocus = true,
  ...props
}: ToolbarPrimitive.Root.Props) {
  return (
    <ToolbarPrimitive.Root
      data-slot="toolbar"
      orientation={orientation}
      loopFocus={loopFocus}
      className={cn(
        'bg-popover text-popover-foreground',
        'z-50 flex w-fit self-start items-center gap-1 rounded-lg',
        'border border-border p-1',
        'shadow-md shadow-black outline-none',
        'data-[orientation=vertical]:flex-col',
        'data-[orientation=vertical]:items-stretch',
        className,
      )}
      {...props}
    />
  )
}

function ToolbarGroup({ className, ...props }: ToolbarPrimitive.Group.Props) {
  return (
    <ToolbarPrimitive.Group
      data-slot="toolbar-group"
      className={cn(
        'flex items-center gap-px',
        'data-[orientation=vertical]:flex-col',
        'data-[orientation=vertical]:items-stretch',
        className,
      )}
      {...props}
    />
  )
}

type ToolbarButtonProps = ToolbarPrimitive.Button.Props &
  VariantProps<typeof buttonVariants>

function ToolbarButton({
  className,
  variant = 'ghost',
  size = 'icon',
  ...props
}: ToolbarButtonProps) {
  return (
    <ToolbarPrimitive.Button
      data-slot="toolbar-button"
      data-variant={variant}
      data-size={size}
      className={cn(
        buttonVariants({ variant, size }),

        // Toolbar specific overrides to button
        'aria-pressed:bg-accent aria-pressed:text-accent-foreground',
        'data-disabled:opacity-50 data-disabled:cursor-not-allowed',

        className,
      )}
      {...props}
    />
  )
}

type ToolbarLinkProps = ToolbarPrimitive.Link.Props &
  VariantProps<typeof buttonVariants>

function ToolbarLink({ className, variant, size, ...props }: ToolbarLinkProps) {
  return (
    <ToolbarPrimitive.Link
      data-slot="toolbar-link"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  )
}

function ToolbarSeparator({
  className,
  ...props
}: ToolbarPrimitive.Separator.Props) {
  return (
    <ToolbarPrimitive.Separator
      data-slot="toolbar-separator"
      className={cn(
        'bg-border shrink-0',
        'data-[orientation=horizontal]:h-px',
        'data-[orientation=horizontal]:w-full',
        'data-[orientation=vertical]:h-5',
        'data-[orientation=vertical]:w-px',
        'data-[orientation=vertical]:mx-0.5',
        className,
      )}
      {...props}
    />
  )
}

function ToolbarInput({ className, ...props }: ToolbarPrimitive.Input.Props) {
  return (
    <ToolbarPrimitive.Input
      data-slot="toolbar-input"
      className={cn(
        'border-input bg-transparent text-foreground',
        'placeholder:text-muted-foreground',
        'h-8 min-w-0 rounded-md border px-2.5 text-sm shadow-xs',
        'outline-none transition-[color,box-shadow]',
        'focus-visible:border-ring',
        'focus-visible:ring-ring/50 focus-visible:ring-[3px]',
        'disabled:pointer-events-none disabled:opacity-50',
        'data-disabled:pointer-events-none data-disabled:opacity-50',
        'aria-invalid:border-destructive',
        'aria-invalid:ring-destructive/20',
        'dark:aria-invalid:ring-destructive/40',
        className,
      )}
      {...props}
    />
  )
}

export {
  Toolbar,
  ToolbarGroup,
  ToolbarButton,
  ToolbarLink,
  ToolbarSeparator,
  ToolbarInput,
  type ToolbarButtonProps,
  type ToolbarLinkProps,
}
